import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Custom event name dispatched saat WS terima payload realtime yang bukan notifikasi
// (mis. task_comment). TaskDetailDialog subscribe supaya thread live update.
export const WS_TASK_COMMENT_EVENT = "ens:task-comment";
import { Bell, BellRing, CheckCheck, X, ChevronRight, Info, Lock, Palmtree, Megaphone, MessageSquare, AtSign, Clock, CheckCircle2, Moon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";

// Category → icon meta
const CAT_META = {
  info:         { icon: Info,          cls: "text-blue-700"    },
  access:       { icon: Lock,          cls: "text-amber-700"   },
  task:         { icon: ChevronRight,  cls: "text-emerald-700" },
  task_comment: { icon: MessageSquare, cls: "text-violet-700"  },
  task_mention: { icon: AtSign,        cls: "text-fuchsia-700" },
  leave:        { icon: Palmtree,      cls: "text-cyan-700"    },
  announcement: { icon: Megaphone,     cls: "text-[#FF5A1F]"    },
  reminder:     { icon: BellRing,      cls: "text-amber-700"    },
};

// Relative time — pure client-side.
const fmtRel = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "baru saja";
  const m = Math.round(s / 60); if (m < 60) return `${m} menit lalu`;
  const h = Math.round(s / 3600); if (h < 24) return `${h} jam lalu`;
  const d = Math.round(s / 86400); if (d < 7) return `${d} hari lalu`;
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
};

// Build wss:// URL from REACT_APP_BACKEND_URL. Auth via HttpOnly cookie yang
// otomatis ikut untuk WS same-origin — tidak perlu token di URL param lagi.
const buildWsUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/api/ws/notifications`;
};

// 149-cz (PROMPT 16-NOTIF-DEEPLINK-R2): shared deep-link builder used by
// NotificationBell + AnnouncementsPage notification list. Returns { route,
// reason }. Prefer explicit fields (task_id, comment_id, target_module) —
// legacy notif tanpa field tsb jatuh ke `link` mentah bila ada, atau
// dilaporkan MISSING_TARGET supaya UI dapat tampilkan pesan bantu.
export function buildNotifTarget(n) {
  if (!n) return { route: null, reason: "EMPTY" };
  // Task-family: task, task_comment, task_mention
  if (n.task_id) {
    const params = new URLSearchParams();
    params.set("task", n.task_id);
    if (n.comment_id) params.set("comment", n.comment_id);
    return { route: `/tasks?${params.toString()}`, reason: "TASK_DEEPLINK" };
  }
  const isTaskFamily = ["task", "task_comment", "task_mention"].includes(n.category);
  if (isTaskFamily && !n.task_id) {
    // Legacy row — tidak ada task_id, jangan tebak dari title (spec).
    return { route: null, reason: "MISSING_TARGET" };
  }
  // 149-cw-r160: Non-task categories — pakai `link` mentah. Kalau ada `comment_id`
  // (mis. petty cash line-item note, tender review comment ke depan), append ke
  // link supaya halaman tujuan dapat scroll ke item spesifik.
  if (n.link) {
    if (n.comment_id) {
      const sep = n.link.includes("?") ? "&" : "?";
      return { route: `${n.link}${sep}comment=${encodeURIComponent(n.comment_id)}`, reason: "LEGACY_LINK_WITH_COMMENT" };
    }
    return { route: n.link, reason: "LEGACY_LINK" };
  }
  return { route: null, reason: "NO_LINK" };
}

export default function NotificationBell() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wrapRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setRows(data.rows || []);
      setUnreadCount(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  // WebSocket connection with fallback to polling. Auth via HttpOnly cookie
  // yang otomatis dikirim untuk same-origin WS handshake.
  useEffect(() => {
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(buildWsUrl());
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          setWsConnected(true);
          // stop polling when WS is live
          if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
          // initial full load so we sync any missed items
          load();
        };

        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === "hello") {
              if (typeof msg.unread_count === "number") setUnreadCount(msg.unread_count);
            } else if (msg.type === "notification" && msg.notification) {
              setRows((xs) => [msg.notification, ...xs].slice(0, 100));
              if (typeof msg.unread_count === "number") setUnreadCount(msg.unread_count);
              else setUnreadCount((c) => c + 1);
            } else if (msg.type === "task_comment" && msg.task_id && msg.comment) {
              // Realtime comment broadcast — dispatch ke window event, TaskDetailDialog
              // yang lagi kebuka akan append comment tanpa polling.
              try {
                window.dispatchEvent(new CustomEvent(WS_TASK_COMMENT_EVENT, {
                  detail: { task_id: msg.task_id, comment: msg.comment },
                }));
              } catch { /* ignore */ }
            } else if (["message_new", "message_edit", "message_read"].includes(msg.type)) {
              // 149-cb: bridge ke MessengerFAB/Drawer via window event bus
              try { window.dispatchEvent(new CustomEvent("ens:ws", { detail: msg })); } catch {}
            }
          } catch { /* ignore malformed */ }
        };

        const scheduleReconnect = () => {
          setWsConnected(false);
          wsRef.current = null;
          // fallback poll while disconnected
          if (!pollTimerRef.current) {
            load();
            pollTimerRef.current = setInterval(load, 60000);
          }
          if (!cancelled) {
            reconnectTimerRef.current = setTimeout(connect, 5000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onclose = scheduleReconnect;
      } catch {
        // Immediate failure — fall back to polling
        setWsConnected(false);
        if (!pollTimerRef.current) {
          load();
          pollTimerRef.current = setInterval(load, 60000);
        }
        if (!cancelled) reconnectTimerRef.current = setTimeout(connect, 5000);
      }
    };

    load();
    connect();

    // heartbeat ping every 25s
    const pingTimer = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) {
        try { ws.send("ping"); } catch {}
      }
    }, 25000);

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      clearInterval(pingTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    };
  }, [load]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setRows((xs) => xs.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {}
    }
    // 149-cz (PROMPT 16-NOTIF-DEEPLINK-R2): shared deep-link resolver.
    // Prefer explicit task_id/comment_id fields (new notif format); fall back
    // to `link` for legacy rows. Notifikasi lama tanpa task_id → tampilkan
    // pesan bantu dan tetap arahkan ke /tasks.
    setOpen(false);
    const target = buildNotifTarget(n);
    if (target.route) {
      nav(target.route);
    } else if (target.reason === "MISSING_TARGET") {
      toast.error("Task atau komentar tujuan tidak dapat ditemukan. Notifikasi lama tanpa referensi eksplisit.");
      if (n.link) nav(n.link);
    }
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setRows((xs) => xs.map((r) => ({ ...r, read: true })));
      setUnreadCount(0);
      toast.success("Semua notifikasi ditandai sudah dibaca");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);
  const hasUnread = unreadCount > 0;

  // 149-aw: Reminder quick actions handler — moved outside render (149-br fix).
  // Pass callback yang menutup dropdown + navigate.
  const handleReminderOpen = useCallback((link) => {
    if (link) { setOpen(false); nav(link); }
  }, [nav]);

  return (
    <div className="relative" ref={wrapRef} data-testid="notif-bell-wrap">
      <button
        type="button"
        data-testid="btn-notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100 transition-colors"
        aria-label="Notifikasi"
        title="Notifikasi"
      >
        <Bell size={18} />
        {/* Small dot in bottom-right shows realtime WS status.
            Emerald = connected (realtime); slate = polling fallback. */}
        <span
          data-testid="notif-ws-status"
          data-ws-connected={wsConnected ? "1" : "0"}
          className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-1 ring-white ${wsConnected ? "bg-emerald-500" : "bg-slate-400"}`}
          title={wsConnected ? "Realtime aktif" : "Polling (fallback)"}
        />
        {hasUnread && (
          <span
            data-testid="notif-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-[#FF5A1F] text-white text-[10px] font-bold tabular-nums flex items-center justify-center leading-none"
          >{badge}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-h-[70vh] overflow-hidden bg-white border border-slate-200 shadow-xl z-50 flex flex-col" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="font-display text-sm tracking-tight text-[#0B1D3A] flex items-center gap-2">
              Notifikasi {hasUnread && <span className="px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-[#FF5A1F] text-white">{badge}</span>}
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="btn-notif-see-all"
                type="button"
                onClick={() => { setOpen(false); nav("/announcements?view=notifications"); }}
                className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:text-[#0B1D3A] flex items-center gap-1"
                title="Lihat semua di halaman Announcements"
              >Lihat Semua</button>
              {hasUnread && (
                <button data-testid="btn-notif-read-all" type="button" onClick={markAllRead} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A] flex items-center gap-1">
                  <CheckCheck size={11} />
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-slate-400 hover:text-slate-700"><X size={13} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                <Bell size={24} className="mx-auto text-slate-300 mb-2" />
                Belum ada notifikasi.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((n) => {
                  const meta = CAT_META[n.category] || CAT_META.info;
                  const Icon = meta.icon;
                  const isReminder = n.category === "reminder";
                  return (
                    <li
                      key={n.id}
                      data-testid={`notif-item-${n.id}`}
                      className={`px-4 py-3 hover:bg-slate-50 flex items-start gap-3 ${!n.read ? "bg-orange-50/40 border-l-2 border-[#FF5A1F]" : ""}`}
                    >
                      <Icon size={14} className={`shrink-0 mt-0.5 ${meta.cls}`} />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => markRead(n)}>
                        <div className={`text-xs ${!n.read ? "font-semibold text-[#0B1D3A]" : "text-slate-700"}`}>{n.title}</div>
                        {n.body && <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{n.body}</div>}
                        <div className="text-[10px] text-slate-400 mt-1 tabular-nums">{fmtRel(n.created_at)}</div>
                        {isReminder && (
                          <ReminderQuickActions notif={n} onDone={load} onOpenLink={handleReminderOpen} />
                        )}
                      </div>
                      {!n.read && <span className="w-2 h-2 bg-[#FF5A1F] shrink-0 mt-1.5" title="Belum dibaca" />}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// 149-aw / 149-br: Reminder quick actions — Snooze 10m/1h/Tomorrow + Tandai Selesai + Buka Detail.
// Extracted dari NotificationBell render supaya tidak recreate komponen setiap render
// (menghindari state loss + optimasi React.memo bisa jalan). Parent memberi callback
// onOpenLink untuk menutup dropdown + navigate.
function ReminderQuickActions({ notif, onDone, onOpenLink }) {
  const [busy, setBusy] = useState(null);
  const [deliveryId, setDeliveryId] = useState(notif.delivery_id || null);
  useEffect(() => {
    if (deliveryId || !notif.link) return;
    const m = /open=([^&]+)/.exec(notif.link || "");
    if (!m) return;
    let alive = true;
    api.get("/reminders/deliveries", { params: { limit: 20 } }).then(({ data }) => {
      if (!alive) return;
      const found = (data.items || []).find((d) => d.event_id === m[1] && d.status !== "cancelled");
      if (found) setDeliveryId(found.id);
    }).catch(() => {});
    return () => { alive = false; };
  }, [deliveryId, notif.link]);

  const run = async (action, duration) => {
    if (action === "open") { onOpenLink && onOpenLink(notif.link); onDone && onDone(); return; }
    if (!deliveryId) { toast.error("Delivery ID tidak tersedia"); return; }
    setBusy(action);
    try {
      if (action === "snooze") {
        await api.post(`/reminders/deliveries/${deliveryId}/snooze`, { duration });
        const label = duration === "10m" ? "10 menit" : duration === "1h" ? "1 jam" : "besok pagi";
        toast.success(`Reminder ditunda ${label}`);
      } else if (action === "complete") {
        await api.post(`/reminders/deliveries/${deliveryId}/complete`);
        toast.success("Event ditandai selesai");
      }
      onDone && onDone();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal");
    } finally { setBusy(null); }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap mt-1.5" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid={`rem-action-open-${notif.id}`} onClick={() => run("open")}
        disabled={busy !== null}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        <ChevronRight size={9} /> Buka
      </button>
      <button type="button" data-testid={`rem-action-complete-${notif.id}`} onClick={() => run("complete")}
        disabled={busy !== null || !deliveryId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
        <CheckCircle2 size={9} /> Selesai
      </button>
      <button type="button" data-testid={`rem-action-snooze-10m-${notif.id}`} onClick={() => run("snooze", "10m")}
        disabled={busy !== null || !deliveryId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-40">
        <Clock size={9} /> 10m
      </button>
      <button type="button" data-testid={`rem-action-snooze-1h-${notif.id}`} onClick={() => run("snooze", "1h")}
        disabled={busy !== null || !deliveryId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-40">
        <Clock size={9} /> 1j
      </button>
      <button type="button" data-testid={`rem-action-snooze-tomorrow-${notif.id}`} onClick={() => run("snooze", "tomorrow")}
        disabled={busy !== null || !deliveryId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-40">
        <Moon size={9} /> Besok
      </button>
    </div>
  );
}

